<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  maxRate: { type: Number, default: 200 },
  timezone: { type: String, default: 'UTC' },
  disabled: { type: Boolean, default: false },
  showConversationStatus: { type: Boolean, default: true },
});
// v-model: { rate_per_minute, window_enabled, window_start, window_end, conversation_status }
const model = defineModel({ type: Object, required: true });
const { t } = useI18n();

const hourOptions = computed(() =>
  Array.from({ length: 25 }, (_, h) => ({
    value: h,
    label: `${String(h).padStart(2, '0')}:00`,
  }))
);
const statusOptions = computed(() => [
  { value: 'pending', label: t('WASAPI_CAMPAIGNS.SETTINGS.PENDING') },
  { value: 'open', label: t('WASAPI_CAMPAIGNS.SETTINGS.OPEN') },
]);

const rateError = computed(() => {
  const r = Number(model.value.rate_per_minute);
  if (!r || r < 1 || r > props.maxRate)
    return t('WASAPI_CAMPAIGNS.SETTINGS.RATE_HELP', { max: props.maxRate });
  return '';
});
const windowError = computed(() => {
  if (!model.value.window_enabled) return '';
  const s = Number(model.value.window_start);
  const e = Number(model.value.window_end);
  return s < e ? '' : t('WASAPI_CAMPAIGNS.SETTINGS.WINDOW_HELP');
});
defineExpose({
  isValid: computed(() => !rateError.value && !windowError.value),
});
</script>

<template>
  <div class="flex flex-col gap-5">
    <Input
      :model-value="model.rate_per_minute"
      type="number"
      :min="1"
      :max="maxRate"
      :label="t('WASAPI_CAMPAIGNS.SETTINGS.RATE_LABEL')"
      :message="
        rateError || t('WASAPI_CAMPAIGNS.SETTINGS.RATE_HELP', { max: maxRate })
      "
      :message-type="rateError ? 'error' : 'info'"
      :disabled="disabled"
      @update:model-value="model.rate_per_minute = Number($event)"
    />
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium text-n-slate-12">{{
          t('WASAPI_CAMPAIGNS.SETTINGS.WINDOW_LABEL')
        }}</label>
        <div class="flex items-center gap-2 text-sm text-n-slate-11">
          <span>{{ t('WASAPI_CAMPAIGNS.SETTINGS.WINDOW_ENABLE') }}</span>
          <Switch v-model="model.window_enabled" :disabled="disabled" />
        </div>
      </div>
      <div v-if="model.window_enabled" class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{
            t('WASAPI_CAMPAIGNS.SETTINGS.WINDOW_START')
          }}</span>
          <ComboBox
            v-model="model.window_start"
            :options="hourOptions.slice(0, 24)"
            :disabled="disabled"
          />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{
            t('WASAPI_CAMPAIGNS.SETTINGS.WINDOW_END')
          }}</span>
          <ComboBox
            v-model="model.window_end"
            :options="hourOptions.slice(1)"
            :disabled="disabled"
          />
        </div>
      </div>
      <p
        class="text-xs"
        :class="windowError ? 'text-n-ruby-11' : 'text-n-slate-11'"
      >
        {{ windowError || t('WASAPI_CAMPAIGNS.SETTINGS.WINDOW_HELP') }}
        {{ t('WASAPI_CAMPAIGNS.SETTINGS.TIMEZONE', { timezone }) }}
      </p>
    </div>
    <div v-if="showConversationStatus" class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">{{
        t('WASAPI_CAMPAIGNS.SETTINGS.CONVERSATION_STATUS')
      }}</label>
      <ComboBox
        v-model="model.conversation_status"
        :options="statusOptions"
        :disabled="disabled"
      />
    </div>
  </div>
</template>
